import { PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE } from '@klear/constants'
import { KlearDate } from '@klear/date'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'

import type { ProofOfAcceptanceCreateWire } from '@klear/proof-of-acceptance/proof-of-acceptance.wire'

export type ProofOfAcceptanceEventData = Omit<ProofOfAcceptanceCreateWire, 'orderId'> & { type: string; id?: number }

export type ProofOfAcceptanceJson = {
  orderId: number
  id?: number
  parentId?: number
  effectiveDate: KlearDate
  notes?: string
}

export class ProofOfAcceptance {
  static readonly __klearClass = 'ProofOfAcceptance'

  readonly orderId: number
  id?: number
  parentId?: number
  effectiveDate: KlearDate
  notes?: string

  constructor(props: ProofOfAcceptanceJson) {
    this.orderId = props.orderId
    this.id = props.id
    this.parentId = props.parentId
    this.effectiveDate = props.effectiveDate
    this.notes = props.notes
  }

  toJSON(): ProofOfAcceptanceJson {
    return {
      orderId: this.orderId,
      id: this.id,
      parentId: this.parentId,
      effectiveDate: this.effectiveDate,
      notes: this.notes
    }
  }

  get isPersisted(): boolean {
    return isNumber(this.id) && this.id > 0
  }

  assertValid(): void {
    if (!(this.orderId > 0)) {
      throw new Error('Proof of acceptance requires an order id')
    }
  }
}

function parseDate(value: unknown, fallback: KlearDate): KlearDate {
  if (value instanceof KlearDate) return value
  if (isString(value) && value.length > 0) {
    const parsed = KlearDate.parse(value)
    if (parsed) return parsed
  }
  return fallback
}

export function proofOfAcceptanceFromEventData(
  orderId: number,
  data: Omit<ProofOfAcceptanceCreateWire, 'orderId'>,
  ctx?: { id?: number; parentEventId?: number }
): ProofOfAcceptance {
  return new ProofOfAcceptance({
    orderId,
    id: ctx?.id,
    parentId: ctx?.parentEventId ?? data.parentEventId,
    effectiveDate: parseDate(data.effectiveDate, KlearDate.today()),
    notes: data.notes ?? undefined
  })
}

export function proofOfAcceptanceToEventData(proof: ProofOfAcceptance): ProofOfAcceptanceEventData {
  const data: ProofOfAcceptanceEventData = {
    type: PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
    effectiveDate: proof.effectiveDate.toJSON()
  }

  if (proof.id != null && proof.id > 0) data.id = proof.id
  if (proof.notes != null) data.notes = proof.notes

  return data
}
